export const COACH_MOBILE_WORKSPACE_HISTORY_KEY = "coach-mobile-workspace";

const COACH_MOBILE_WORKSPACE_BASE_HISTORY_KEY = "coach-mobile-workspace-base";

export type CoachMobileWorkspaceHistoryOwner = "coach-veera" | "page-workspace";

interface HistoryEntrySnapshot {
  readonly state: unknown;
  readonly url: string;
}

interface BaseHistoryEntry extends HistoryEntrySnapshot {
  readonly identity: string;
}

interface ActiveWorkspaceHistory {
  readonly token: symbol;
  readonly owner: CoachMobileWorkspaceHistoryOwner;
  readonly marker: string;
  readonly onBack: () => void;
  base: BaseHistoryEntry;
  boundaryReady: boolean;
}

interface PendingCleanup {
  readonly base: BaseHistoryEntry;
  requested?: HistoryEntrySnapshot;
}

let activeWorkspace: ActiveWorkspaceHistory | null = null;
let pendingCleanup: PendingCleanup | null = null;
let listening = false;
let observedPushState: History["pushState"] | null = null;
let observedReplaceState: History["replaceState"] | null = null;

const readHistoryState = (state: unknown): Record<string, unknown> => (
  state && typeof state === "object" && !Array.isArray(state)
    ? state as Record<string, unknown>
    : {}
);

const withoutInternalHistoryState = (state: unknown): unknown => {
  if (!state || typeof state !== "object" || Array.isArray(state)) return state;
  const cleanState = { ...state as Record<string, unknown> };
  delete cleanState[COACH_MOBILE_WORKSPACE_HISTORY_KEY];
  delete cleanState[COACH_MOBILE_WORKSPACE_BASE_HISTORY_KEY];
  return cleanState;
};

const createIdentity = (owner: CoachMobileWorkspaceHistoryOwner | "base") => (
  `${owner}-${Date.now()}-${Math.random()}`
);

const currentEntry = (): HistoryEntrySnapshot => ({
  state: withoutInternalHistoryState(window.history.state),
  url: window.location.href,
});

const nativePushState = (state: unknown, url?: string) => {
  const pushState = observedPushState ?? window.history.pushState;
  pushState.call(window.history, state, "", url);
};

const nativeReplaceState = (state: unknown, url?: string) => {
  const replaceState = observedReplaceState ?? window.history.replaceState;
  replaceState.call(window.history, state, "", url);
};

const modalState = (state: unknown, marker: string) => ({
  ...readHistoryState(withoutInternalHistoryState(state)),
  [COACH_MOBILE_WORKSPACE_HISTORY_KEY]: marker,
});

const taggedBaseState = (state: unknown, identity: string) => ({
  ...readHistoryState(withoutInternalHistoryState(state)),
  [COACH_MOBILE_WORKSPACE_BASE_HISTORY_KEY]: identity,
});

const installActiveBoundary = (
  active: ActiveWorkspaceHistory,
  base: HistoryEntrySnapshot,
) => {
  const cleanBase: HistoryEntrySnapshot = {
    state: withoutInternalHistoryState(base.state),
    url: base.url,
  };
  const taggedBase: BaseHistoryEntry = {
    ...cleanBase,
    identity: createIdentity("base"),
  };
  nativeReplaceState(taggedBaseState(cleanBase.state, taggedBase.identity), cleanBase.url);
  nativePushState(modalState(cleanBase.state, active.marker));
  active.base = taggedBase;
  active.boundaryReady = true;
};

const pushActiveBoundaryFromTaggedBase = (
  active: ActiveWorkspaceHistory,
  base: BaseHistoryEntry,
) => {
  nativePushState(modalState(base.state, active.marker));
  active.base = base;
  active.boundaryReady = true;
};

const stopObservingHistoryMutations = () => {
  if (observedPushState) window.history.pushState = observedPushState;
  if (observedReplaceState) window.history.replaceState = observedReplaceState;
  observedPushState = null;
  observedReplaceState = null;
};

const startObservingHistoryMutations = () => {
  if (observedPushState || observedReplaceState) return;
  observedPushState = window.history.pushState;
  observedReplaceState = window.history.replaceState;

  window.history.pushState = (data, unused, url) => {
    observedPushState?.call(window.history, data, unused, url);
    if (pendingCleanup) pendingCleanup.requested = currentEntry();
  };
  window.history.replaceState = (data, unused, url) => {
    observedReplaceState?.call(window.history, data, unused, url);
    if (pendingCleanup) pendingCleanup.requested = currentEntry();
  };
};

const stopListeningWhenIdle = () => {
  if (!listening || activeWorkspace || pendingCleanup) return;
  window.removeEventListener("popstate", handlePopState, true);
  listening = false;
  stopObservingHistoryMutations();
};

const isExactBaseEntry = (state: unknown, base: BaseHistoryEntry) => {
  const historyState = readHistoryState(state);
  return (
    historyState[COACH_MOBILE_WORKSPACE_BASE_HISTORY_KEY] === base.identity &&
    historyState[COACH_MOBILE_WORKSPACE_HISTORY_KEY] === undefined
  );
};

const finishPendingCleanup = (pending: PendingCleanup, event: PopStateEvent) => {
  const requested = pending.requested;
  const replacement = activeWorkspace;

  if (requested) {
    nativeReplaceState(pending.base.state, pending.base.url);
    nativePushState(requested.state, requested.url);
    if (replacement) installActiveBoundary(replacement, requested);
  } else if (replacement) {
    pushActiveBoundaryFromTaggedBase(replacement, pending.base);
  } else {
    nativeReplaceState(pending.base.state, pending.base.url);
  }

  pendingCleanup = null;
  stopObservingHistoryMutations();
  if (requested || replacement) event.stopImmediatePropagation();
  stopListeningWhenIdle();
};

function handlePopState(event: PopStateEvent) {
  const pending = pendingCleanup;
  if (pending) {
    if (!isExactBaseEntry(event.state, pending.base)) {
      event.stopImmediatePropagation();
      window.history.back();
      return;
    }
    finishPendingCleanup(pending, event);
    return;
  }

  const active = activeWorkspace;
  if (!active) {
    stopListeningWhenIdle();
    return;
  }
  if (event.state?.[COACH_MOBILE_WORKSPACE_HISTORY_KEY] === active.marker) return;

  if (isExactBaseEntry(event.state, active.base)) {
    nativeReplaceState(active.base.state, active.base.url);
  }
  activeWorkspace = null;
  active.onBack();
  stopListeningWhenIdle();
}

const ensureListening = () => {
  if (listening) return;
  window.addEventListener("popstate", handlePopState, true);
  listening = true;
};

export const claimCoachMobileWorkspaceHistory = (
  owner: CoachMobileWorkspaceHistoryOwner,
  onBack: () => void,
) => {
  const token = Symbol(owner);
  const current = activeWorkspace;
  const marker = current?.marker ?? createIdentity(owner);
  const boundaryReady = current?.boundaryReady ?? !pendingCleanup;
  const base = current?.base ?? pendingCleanup?.base ?? {
    ...currentEntry(),
    identity: createIdentity("base"),
  };

  const active: ActiveWorkspaceHistory = {
    token,
    owner,
    marker,
    onBack,
    base,
    boundaryReady,
  };
  activeWorkspace = active;
  ensureListening();

  if (!current && !pendingCleanup) installActiveBoundary(active, base);

  return () => {
    const currentActive = activeWorkspace;
    if (!currentActive || currentActive.token !== token) return;
    activeWorkspace = null;

    if (currentActive.boundaryReady) {
      const currentState = window.history.state;
      if (isExactBaseEntry(currentState, currentActive.base)) {
        nativeReplaceState(currentActive.base.state, currentActive.base.url);
        stopListeningWhenIdle();
        return;
      }

      pendingCleanup = {
        base: currentActive.base,
        ...(currentState?.[COACH_MOBILE_WORKSPACE_HISTORY_KEY] === currentActive.marker
          ? {}
          : { requested: currentEntry() }),
      };
      startObservingHistoryMutations();
      window.history.back();
      return;
    }
    stopListeningWhenIdle();
  };
};

export const resetCoachMobileWorkspaceHistoryForTests = () => {
  activeWorkspace = null;
  pendingCleanup = null;
  stopObservingHistoryMutations();
  if (listening) window.removeEventListener("popstate", handlePopState, true);
  listening = false;
};
